// Installed → "Button rows": find every device missing its button rows, and
// create the missing ones. The I/O half; the rules are in
// core/devices/outputRows.js and the account of why this exists is there too.
//
// ⚠️ IT ONLY EVER INSERTS. A device with MORE rows than its type expects is
// reported and left alone — the backend's old repair cron deleted every row of
// such a device and recreated them as `O_<uno>_<n>`, i.e. it threw away the
// customer's button names to fix a count. Never again (asynccontrollers.js,
// CheckForMissingOutputDetails, the removed `else`).
//
// ⚠️ THE COUNT IS SHOWN BEFORE THE WRITE, the way "Remove room" and Unassign
// do: the plan is read per device (which output numbers are actually missing),
// then the operator confirms, then it writes.

import {
  getDevicesMissingOutputRows, getOutputsFresh, addOutputRow,
} from '../api/api';
import { missingOutputs, repairPlan, freshOutputName } from '../devices/outputRows';
import { invalidateNameIndex } from '../devices/nameIndex';
import { markUsersOfDevice } from '../db/markSinkData';

const rows = (res) => (Array.isArray(res?.data) ? res.data : []);

/**
 * The devices to offer, each with the exact output numbers it is missing.
 *
 * The server's one query gives a COUNT; which numbers are absent needs the
 * device's own rows, so those are read per device, a few at a time (the same
 * courtesy the fleet pages show). A device whose rows cannot be read is kept
 * with `missing: null` and is never written to.
 */
export async function loadOutputRowRepair({ concurrency = 6, onProgress } = {}) {
  const plan = repairPlan(rows(await getDevicesMissingOutputRows()));
  for (let i = 0; i < plan.length; i += concurrency) {
    const batch = plan.slice(i, i + concurrency);
    /* eslint-disable no-await-in-loop */
    const res = await Promise.all(batch.map((d) => getOutputsFresh(d.UniqueNumber).catch(() => null)));
    /* eslint-enable no-await-in-loop */
    batch.forEach((d, k) => {
      if (!res[k]) { d.missing = null; return; }
      d.missing = missingOutputs(d.expected, rows(res[k]));
      d.ticked = d.ticked && d.missing.length > 0;
    });
    if (onProgress) onProgress(Math.min(plan.length, i + batch.length), plan.length);
  }
  return plan.filter((d) => d.missing == null || d.missing.length > 0);
}

/**
 * Create the missing rows of one device.
 *
 * ⚠️ THE NAME IS THE BACKEND'S DEFAULT, `O_<uno>_<n>` — never a guess at what
 * the button is. The customer renames it afterwards, and now the rename has a
 * row to land on, which is the entire point.
 *
 * -> { uno, created, failed: [{ output, error }] }
 */
export async function repairDeviceOutputRows(device) {
  const uno = String(device.UniqueNumber);
  const installedId = Number(device.ID) || 0;
  const out = { uno, created: 0, failed: [] };
  if (!installedId) {
    out.failed.push({ output: 0, error: 'no DeviceInstalledId on the device row' });
    return out;
  }
  // eslint-disable-next-line no-restricted-syntax
  for (const output of device.missing || []) {
    /* eslint-disable no-await-in-loop */
    try {
      const res = await addOutputRow(uno, output, freshOutputName(uno, output), installedId);
      // ⚠️ 200 + { status: '500' } is this backend's failure shape.
      if (String(res?.data?.status) === '500') throw new Error('the server refused the insert');
      out.created += 1;
    } catch (e) {
      out.failed.push({ output, error: e?.message || 'failed' });
    }
    /* eslint-enable no-await-in-loop */
  }
  if (out.created) {
    invalidateNameIndex();
    // Those rows are in every other phone's cached output list.
    await markUsersOfDevice(uno).catch(() => {});
  }
  return out;
}

/**
 * Run the repair over the ticked devices, reporting as it goes.
 * -> { devices, created, failed: [{ uno, output, error }] }
 */
export async function runOutputRowRepair(plan, { onProgress } = {}) {
  const picked = (plan || []).filter((d) => d.ticked && Array.isArray(d.missing) && d.missing.length);
  const result = { devices: 0, created: 0, failed: [] };
  for (let i = 0; i < picked.length; i += 1) {
    /* eslint-disable no-await-in-loop */
    const one = await repairDeviceOutputRows(picked[i]);
    /* eslint-enable no-await-in-loop */
    result.devices += 1;
    result.created += one.created;
    one.failed.forEach((f) => result.failed.push({ uno: one.uno, ...f }));
    if (onProgress) onProgress(i + 1, picked.length, one);
  }
  return result;
}

const outputRowRepair = { loadOutputRowRepair, repairDeviceOutputRows, runOutputRowRepair };
export default outputRowRepair;
